import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { estadoDelPrestamo, siguienteCorte } from '../utils/prestamo';
import { mensajeDeEstado, enlaceDeWhatsApp, fechaLarga,
         opcionesDeLosOtros } from '../utils/estadoDeCuenta';

const moneda = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');

// A quién le cerró el mes y hay que mandarle su cuenta.
//
// La lista se ordena por fecha de corte y arranca en los de HOY, porque el
// estado de cuenta solo tiene sentido justo después del corte: antes, lo que el
// cliente entregó está en espera y aparecería como si no se lo hubieran anotado.
//
// Con los cortes repartidos por todo el mes —cada préstamo tiene el suyo— salen
// seis o siete al día, no ciento ochenta de golpe.
//
// El mensaje NO se manda solo: se abre WhatsApp con el texto escrito y quien
// cobra le da a enviar. Una cifra equivocada mandada sola no se puede recoger.

const CAJA = {
  background: 'white', borderRadius: '10px', padding: '16px',
  boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: '16px',
};

const diaDe = (d) => Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 86400000);
const hoyDia = () => { const n = new Date(); return Math.floor(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate()) / 86400000); };

function EstadosDeCuenta({ ajustes }) {
  const [prestamos, setPrestamos] = useState(null);
  // Préstamos que el cliente canceló hace poco. No están en la lista de arriba
  // —esa es de activos— y sin ellos el mensaje no puede cuadrar lo que entregó.
  const [cancelados, setCancelados] = useState([]);
  const [error, setError] = useState('');
  // ARRANCA EN LA SEMANA, no en «hoy».
  //
  // Cada préstamo tiene su propio corte y están repartidos por todo el mes, así
  // que «hoy» deja la pantalla vacía la mayoría de los días. El del Dr. Suárez
  // cortó el 20 y el 22 de septiembre: el 24 no aparecía, y quien lo buscaba
  // concluía que el programa no lo tenía.
  const [dias, setDias] = useState(7);
  const [viendo, setViendo] = useState(null);
  const [enviados, setEnviados] = useState(() => {
    try { return JSON.parse(localStorage.getItem('estados-enviados') || '{}'); }
    catch { return {}; }
  });

  const saludo = (ajustes && ajustes.saludo) || 'Buenas';
  const cierre = (ajustes && ajustes.cierre) || 'Cualquier duda me escribe.';

  useEffect(() => {
    let vivo = true;
    axios.get('/api/delinquents')
      .then(r => {
        if (!vivo) return;
        setPrestamos(r.data?.prestamos || []);
        setCancelados(r.data?.cancelados || []);
      })
      .catch(() => { if (vivo) setError('No se pudo cargar la lista'); });
    return () => { vivo = false; };
  }, []);

  const filas = useMemo(() => {
    if (!prestamos) return [];
    const hoy = hoyDia();
    return prestamos.map(p => {
      const estado = estadoDelPrestamo(p, p.pagos || []);
      const liqs = estado.liquidaciones || [];
      const ultima = liqs.length ? liqs[liqs.length - 1] : null;
      if (!ultima) return null;
      const diasDesdeCorte = hoy - diaDe(ultima.corte);
      return { p, estado, ultima, diasDesdeCorte,
               proximo: siguienteCorte(p, new Date()) };
    })
      .filter(f => f && f.diasDesdeCorte >= 0 && f.diasDesdeCorte <= dias)
      .sort((a, b) => a.diasDesdeCorte - b.diasDesdeCorte);
  }, [prestamos, dias]);

  const marcar = (id) => {
    const nuevo = { ...enviados, [id]: new Date().toISOString().slice(0, 10) };
    setEnviados(nuevo);
    localStorage.setItem('estados-enviados', JSON.stringify(nuevo));
  };

  // Cuántos préstamos activos tiene cada cliente y cuánto suman.
  //
  // Javier Suárez tenía dos créditos y el mensaje solo hablaba de uno: terminaba
  // en «Debe hoy: $3.156.870» y él creía que ésa era toda su deuda. Cada fila de
  // esta pantalla ES un préstamo, así que sin agrupar por cliente el mensaje no
  // tiene forma de saber que hay otro.
  //
  // `id` es el del CLIENTE y `loan_id` el del préstamo — vienen así del
  // servidor, y confundirlos aquí agruparía cada préstamo consigo mismo.
  // Se guarda CADA préstamo, no solo cuántos: el mensaje los nombra uno por uno
  // con su fecha y su corte. Contarlos —«uno de sus 2 préstamos»— dejaba al
  // cliente sin saber cuál era el otro, y volvía a llamar para preguntarlo.
  const porCliente = useMemo(() => {
    const m = {};
    (prestamos || []).forEach(p => {
      (m[p.id] ||= []).push({ prestamo: p, pagos: p.pagos || [],
                              estado: estadoDelPrestamo(p, p.pagos || []) });
    });
    return m;
  }, [prestamos]);

  const armar = (f) => {
    // Los que ESTE cliente canceló. El mensaje se queda solo con los del mismo
    // día que sus abonos: esa parte la decide él, que es quien sabe de qué
    // fechas está hablando.
    const suyos = cancelados.filter(x => String(x.client_id) === String(f.p.id));
    // Los otros préstamos y el total salen de la MISMA función que usa Deuda
    // del cliente. Armados aquí a mano, las dos pantallas mandaban mensajes
    // distintos del mismo cliente según por dónde se le escribiera.
    const deLosOtros = opcionesDeLosOtros(
      porCliente[f.p.id] || [], { prestamo: f.p });
    return mensajeDeEstado(
      { name: f.p.name }, f.p, f.p.pagos || [],
      { saludo, cierre, proximoCorte: f.proximo,
        cancelados: suyos, ...deLosOtros });
  };

  if (error) return <div style={{ color: '#b91c1c' }}>{error}</div>;
  if (!prestamos) return <p style={{ color: '#6b7280' }}>Cargando…</p>;

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>💬 Estados de cuenta</h2>
      <p style={{ color: '#6b7280', marginTop: '-8px' }}>
        A quién le cerró el mes y hay que mandarle su cuenta
      </p>

      <div style={{ ...CAJA, display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '14px' }}>Mostrar los que cerraron</span>
        <select value={dias} onChange={e => setDias(Number(e.target.value))}
                style={{ padding: '6px 10px', borderRadius: '6px',
                         border: '1px solid #d1d5db', fontSize: '14px' }}>
          <option value={0}>hoy</option>
          <option value={2}>en los últimos 3 días</option>
          <option value={7}>en la última semana</option>
          <option value={31}>en el último mes</option>
        </select>
        <span style={{ marginLeft: 'auto', fontWeight: 700, color: '#374151' }}>
          {filas.length} {filas.length === 1 ? 'cliente' : 'clientes'}
        </span>
      </div>

      {filas.length === 0 ? (
        <div style={{ ...CAJA, textAlign: 'center', color: '#6b7280' }}>
          Hoy no le cerró el mes a nadie. Los cortes van repartidos: cada préstamo
          cierra el día que se prestó.
          <div style={{ fontSize: '13px', marginTop: '8px' }}>
            ¿Busca a alguien en concreto? Cambie el selector de arriba, o
            mírelo en <strong>Deuda</strong>: ahí sale cualquier cliente
            cualquier día, con lo que debe hoy.
          </div>
        </div>
      ) : filas.map(f => {
        const enviado = enviados[f.p.loan_id];
        // La cifra del corte NO es lo que debe ahora: si abonó después, ya no
        // debe eso. Se enseña la misma que la pantalla de Deuda y la misma con
        // la que cierra el mensaje —tres sitios, un número—.
        const debe = f.estado.deudaTotal;
        const alCorte = f.ultima.saldoFinal + f.ultima.quedaDebiendoInteres;
        const url = enlaceDeWhatsApp(f.p.phone, armar(f));
        return (
          <div key={f.p.loan_id} style={{ ...CAJA, opacity: enviado ? 0.6 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          gap: '12px', flexWrap: 'wrap', alignItems: 'baseline' }}>
              <div>
                <strong style={{ fontSize: '16px' }}>{f.p.name}</strong>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  Cerró el {fechaLarga(f.ultima.corte)}
                  {f.diasDesdeCorte === 0
                    ? ' · hoy'
                    : ` · hace ${f.diasDesdeCorte} ${f.diasDesdeCorte === 1 ? 'día' : 'días'}`}
                  {f.p.phone ? ` · ${f.p.phone}` : ' · sin teléfono'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 800, fontSize: '17px' }}>{moneda(debe)}</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>debe hoy</div>
                {f.estado.enEspera > 0 && (
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>
                    cerró en {moneda(alCorte)}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap',
                          margin: '10px 0', fontSize: '13px', color: '#4b5563' }}>
              <span>Abonó <strong>{moneda(f.ultima.acumulado)}</strong></span>
              <span>→ interés <strong style={{ color: '#b45309' }}>{moneda(f.ultima.aInteres)}</strong></span>
              <span>→ deuda <strong style={{ color: '#15803d' }}>{moneda(f.ultima.aCapital)}</strong></span>
              {f.ultima.quedaDebiendoInteres > 0 && (
                <span style={{ color: '#b91c1c' }}>
                  quedó debiendo {moneda(f.ultima.quedaDebiendoInteres)} de interés
                </span>
              )}
              {f.estado.enEspera > 0 && (
                <span style={{ color: '#15803d' }}>
                  después del corte abonó <strong>{moneda(f.estado.enEspera)}</strong>
                </span>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {url ? (
                <a href={url} target="_blank" rel="noopener noreferrer"
                   onClick={() => marcar(f.p.loan_id)}
                   style={{ padding: '8px 14px', borderRadius: '6px', background: '#25D366',
                            color: 'white', fontWeight: 700, fontSize: '13px',
                            textDecoration: 'none' }}>
                  💬 Mandarle su cuenta
                </a>
              ) : (
                <span style={{ padding: '8px 14px', borderRadius: '6px', background: '#f3f4f6',
                               color: '#9ca3af', fontSize: '13px' }}>
                  Sin teléfono registrado
                </span>
              )}
              <button onClick={() => setViendo(viendo === f.p.loan_id ? null : f.p.loan_id)}
                      style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid #d1d5db',
                               background: 'white', cursor: 'pointer', fontSize: '13px' }}>
                {viendo === f.p.loan_id ? 'Ocultar' : 'Ver el mensaje'}
              </button>
              {enviado && (
                <span style={{ alignSelf: 'center', fontSize: '12px', color: '#15803d' }}>
                  ✓ marcado como enviado
                </span>
              )}
            </div>

            {viendo === f.p.loan_id && (
              <pre style={{ marginTop: '12px', padding: '14px', background: '#f9fafb',
                            border: '1px solid #e5e7eb', borderRadius: '8px',
                            whiteSpace: 'pre-wrap', fontSize: '13px', lineHeight: 1.5,
                            fontFamily: 'inherit' }}>
                {armar(f)}
              </pre>
            )}
          </div>
        );
      })}

      {filas.length > 0 && (
        <p style={{ fontSize: '13px', color: '#6b7280' }}>
          El botón abre WhatsApp con el mensaje escrito; usted le da a enviar. Nada
          sale solo. La marca de «enviado» se guarda en este aparato, para no
          repetirle a nadie.
        </p>
      )}
    </div>
  );
}

export default EstadosDeCuenta;
